import { Button } from "@shared-ui/button";
import { Input } from "@shared-ui/input";
import { Label } from "@shared-ui/label";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Dialog } from "@/components/ui/dialog";
import { duplicateAdminProduct } from "@/features/catalog/services/catalog-api.service";
import { apiErrorMessage } from "@/lib/api-error";

/**
 * Copy a product, to catalogue a range without retyping it.
 *
 * The endpoint has existed since plan 008 and had no caller. That is the whole
 * reason this file exists: a variant range -- the same tile in six sizes -- is
 * one product's worth of specifications repeated with a different SKU, and
 * without this an operator retypes all of it.
 *
 * WHAT A DUPLICATE DOES NOT INHERIT is the part worth stating, because it is the
 * question an operator will have. `duplicateProduct` copies by OMISSION: it
 * names the fields to drop and carries everything else, so specifications, tier,
 * images and coverage figures come along, while status, verification and the
 * provenance columns do not. A copy is always a draft. There is no field on this
 * form that could change that and none on the route schema either, so the dialog
 * can say so flatly.
 */

const TITLE_ID = "duplicate-product-title";

interface DuplicateProductDialogProps {
	productId: string;
	productName: string;
	onClose: () => void;
	onDuplicated: (newProductId: string) => void;
}

export function DuplicateProductDialog({
	productId,
	productName,
	onClose,
	onDuplicated,
}: DuplicateProductDialogProps) {
	const [sku, setSku] = useState("");
	/*
	 * LEFT EMPTY ON PURPOSE, and optional on the server.
	 *
	 * `duplicateProduct` defaults an absent name to a truncated "Copy of X".
	 * Requiring a name here would mean that default is never exercised by the
	 * only caller it has -- and an operator duplicating a tile into six sizes
	 * wants to type six SKUs, not six names they will edit afterwards anyway.
	 */
	const [name, setName] = useState("");
	const [brandSku, setBrandSku] = useState("");

	const duplicate = useMutation({
		mutationFn: async () =>
			await duplicateAdminProduct(productId, {
				sku: sku.trim(),
				...(name.trim() ? { name: name.trim() } : {}),
				...(brandSku.trim() ? { brandSku: brandSku.trim() } : {}),
			}),
		onSuccess: (product) => onDuplicated(product.id),
	});

	const canSave = sku.trim() !== "" && !duplicate.isPending;

	return (
		<Dialog
			open
			titleId={TITLE_ID}
			onClose={() => {
				if (!duplicate.isPending) onClose();
			}}
			className="grid w-full max-w-lg gap-4 rounded-3xl border bg-white p-5 shadow-xl"
		>
			<div>
				<h2 id={TITLE_ID} className="text-lg font-semibold">
					Duplicate {productName}
				</h2>
				<p className="text-sm text-muted-foreground">
					The copy carries this product's specifications, tier and images. It is
					created as a draft and is not verified, whatever this one is.
				</p>
			</div>

			<div className="grid gap-1.5">
				<Label htmlFor="duplicate-sku">SKU</Label>
				<Input
					id="duplicate-sku"
					value={sku}
					onChange={(event) => setSku(event.target.value)}
				/>
				<p className="text-xs text-muted-foreground">
					Must be unique. It is how BOQ templates and both seed scripts find a
					product.
				</p>
			</div>

			<div className="grid gap-1.5">
				<Label htmlFor="duplicate-name">Name (optional)</Label>
				<Input
					id="duplicate-name"
					value={name}
					onChange={(event) => setName(event.target.value)}
					placeholder={`Copy of ${productName}`}
				/>
			</div>

			<div className="grid gap-1.5">
				<Label htmlFor="duplicate-brand-sku">Brand SKU (optional)</Label>
				<Input
					id="duplicate-brand-sku"
					value={brandSku}
					onChange={(event) => setBrandSku(event.target.value)}
				/>
				<p className="text-xs text-muted-foreground">
					The brand's own code for the copy. It is not carried over: the copy
					keeps this product's brand, and one brand cannot have two products
					under the same code.
				</p>
			</div>

			{duplicate.isError ? (
				/*
				 * The server's own sentence. A duplicate SKU is the failure this
				 * dialog will actually produce -- an operator copying a range types
				 * the same SKU twice -- and the refusal names the product already
				 * holding it, which a generic message would throw away.
				 */
				<p role="alert" className="text-sm text-red-700">
					{apiErrorMessage(
						duplicate.error,
						"Could not duplicate this product.",
					)}
				</p>
			) : null}

			<div className="flex justify-end gap-2 border-t pt-3">
				<Button
					variant="ghost"
					onClick={onClose}
					disabled={duplicate.isPending}
				>
					Cancel
				</Button>
				<Button onClick={() => duplicate.mutate()} disabled={!canSave}>
					{duplicate.isPending ? "Duplicating…" : "Duplicate"}
				</Button>
			</div>
		</Dialog>
	);
}
